import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Zap, Star, ArrowRight, Sparkles } from 'lucide-react';

const Home: React.FC = () => {
  const stats = [
    { value: '50K+', label: 'Mockups Created' },
    { value: '10K+', label: 'Happy Brands' },
    { value: '4.9/5', label: 'User Rating' },
    { value: '30s', label: 'Average Time' }
  ];

  const features = [
    {
      icon: '⚡',
      title: 'Lightning Fast',
      description: 'Generate professional mockups in under 30 seconds with AI'
    },
    {
      icon: '🎨',
      title: '22+ Templates',
      description: 'From books to billboards, we have every mockup you need'
    },
    {
      icon: '🤖',
      title: 'AI-Powered',
      description: 'Google Gemini AI creates photorealistic mockups instantly'
    },
    {
      icon: '💎',
      title: 'Studio Quality',
      description: 'Professional-grade mockups that rival expensive photoshoots'
    }
  ];

  const templates = [
    { id: 'mug', icon: '☕', name: 'Coffee Mug' },
    { id: 'tshirt', icon: '👕', name: 'T-Shirt' },
    { id: 'laptop', icon: '💻', name: 'Laptop' },
    { id: 'business-card', icon: '🪪', name: 'Business Card' },
    { id: 'book', icon: '📚', name: 'Book Cover' },
    { id: 'billboard', icon: '🏗️', name: 'Billboard' }
  ];

  const testimonials = [
    {
      name: 'Sarah Chen',
      role: 'Creative Director',
      company: 'DesignCo',
      text: 'This tool saved us thousands on product photography. The AI-generated mockups are indistinguishable from real photos!',
      avatar: '👩‍💼'
    },
    {
      name: 'Marcus Johnson',
      role: 'E-commerce Owner',
      company: 'TechGear Pro',
      text: 'Game changer for my online store. I can create product mockups in seconds instead of waiting days for photographers.',
      avatar: '👨‍💼'
    },
    {
      name: 'Emily Rodriguez',
      role: 'Marketing Manager',
      company: 'BrandForward',
      text: 'The quality is unbelievable. Our social media engagement increased 3x after using these professional mockups.',
      avatar: '👩‍💻'
    }
  ];

  const faqs = [
    {
      question: 'How does AI mockup generation work?',
      answer: 'Simply upload your logo or design, choose a template, customize colors and text, and our AI creates a photorealistic mockup in seconds.'
    },
    {
      question: 'What file formats are supported?',
      answer: 'We support PNG, JPG, SVG, and WebP formats for uploads. You can download your mockups in PNG or JPG.'
    },
    {
      question: 'Can I use these mockups commercially?',
      answer: 'Yes! All generated mockups are yours to use for any purpose, including commercial projects.'
    },
    {
      question: 'Do I need design experience?',
      answer: 'Not at all! Our intuitive interface makes it easy for anyone to create professional mockups in minutes.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="relative max-w-content mx-auto px-6 py-24 lg:py-32">
          <div className="text-center">
            {/* Trust Badge */}
            <div className="inline-flex items-center gap-2 bg-white text-brand-purple px-6 py-2 rounded-full text-label font-body shadow-card mb-8">
              <Sparkles className="w-4 h-4" />
              <span>Trusted by 10,000+ brands worldwide</span>
            </div>
            
            {/* Headline */}
            <h1 className="font-heading text-h1 text-text-dark mb-6 leading-tight">
              Create Stunning<br />
              <span className="bg-gradient-to-r from-brand-purple to-brand-blue bg-clip-text text-transparent">
                Product Mockups
              </span><br />
              in Seconds
            </h1>
            
            {/* Subheadline */}
            <p className="text-xl text-body-grey mb-10 max-w-3xl mx-auto font-body">
              Transform your logo into professional mockups with AI. No design skills needed. 
              Just upload, customize, and download studio-quality visuals.
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 px-8 py-4 bg-accent-gradient text-white font-body font-medium text-button-lg rounded-lg hover:shadow-lg transform hover:scale-105 transition-all"
              >
                <Zap className="w-5 h-5" />
                Get Started - It's Free
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-brand-purple font-body font-medium text-button-lg rounded-lg border border-brand-purple hover:bg-primary-50 transition-all"
              >
                Sign In to Your Account
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 max-w-4xl mx-auto">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="font-heading text-h2 font-bold text-text-dark">{stat.value}</div>
                  <div className="text-label text-body-grey font-body">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Bar */}
      <section className="bg-white py-8 border-y border-gray-200">
        <div className="max-w-content mx-auto px-6">
          <div className="flex items-center justify-center gap-8 flex-wrap">
            <p className="text-body-grey font-body font-medium">Loved by teams at:</p>
            <div className="flex gap-8 items-center opacity-60">
              <span className="text-2xl font-bold text-body-grey">TechCorp</span>
              <span className="text-2xl font-bold text-body-grey">DesignHub</span>
              <span className="text-2xl font-bold text-body-grey">BrandCo</span>
              <span className="text-2xl font-bold text-body-grey">StartupX</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6">
        <div className="max-w-content mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-h2 text-text-dark mb-4">
              Why Brands Choose BrandScapes AI
            </h2>
            <p className="text-body text-body-grey max-w-3xl mx-auto font-body">
              Professional mockups that used to cost hundreds and take days, now available instantly
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature) => (
              <div key={feature.title} className="bg-white rounded-card p-8 shadow-card hover:shadow-lg transition-shadow">
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="font-heading text-h3 mb-2 text-text-dark">{feature.title}</h3>
                <p className="text-body text-body-grey font-body">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Templates Showcase */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-content mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-h2 text-text-dark mb-4">
              22+ Professional Templates
            </h2>
            <p className="text-body text-body-grey max-w-3xl mx-auto font-body">
              From product packaging to marketing materials, create any mockup you need
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-12">
            {templates.map((template) => (
              <div key={template.id} className="bg-background rounded-card p-6 text-center hover:shadow-card transition-all cursor-pointer group">
                <div className="text-5xl mb-2 transform group-hover:scale-110 transition-transform">{template.icon}</div>
                <p className="text-label font-body text-text-dark">{template.name}</p>
              </div>
            ))}
          </div>
          
          <div className="text-center">
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 text-brand-purple font-body font-medium hover:text-brand-blue transition-colors"
            >
              Explore All Templates
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6">
        <div className="max-w-content mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-h2 text-text-dark mb-4">
              How It Works
            </h2>
            <p className="text-body text-body-grey font-body">
              Three simple steps to professional mockups
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            <div className="text-center">
              <div className="w-20 h-20 bg-primary-100 text-brand-purple rounded-card flex items-center justify-center text-h2 font-heading font-bold mx-auto mb-6">
                1
              </div>
              <h3 className="font-heading text-h3 mb-3 text-text-dark">Upload Your Design</h3>
              <p className="text-body text-body-grey font-body">Drop your logo, image, or brand asset. We support all major formats.</p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-primary-100 text-brand-purple rounded-card flex items-center justify-center text-h2 font-heading font-bold mx-auto mb-6">
                2
              </div>
              <h3 className="font-heading text-h3 mb-3 text-text-dark">Choose & Customize</h3>
              <p className="text-body text-body-grey font-body">Select from 22+ templates and customize colors, text, and style.</p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-primary-100 text-brand-purple rounded-card flex items-center justify-center text-h2 font-heading font-bold mx-auto mb-6">
                3
              </div>
              <h3 className="font-heading text-h3 mb-3 text-text-dark">Download HD Mockup</h3>
              <p className="text-body text-body-grey font-body">Get your AI-generated mockup in high resolution, ready to use anywhere.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-content mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-h2 text-text-dark mb-4">
              Loved by Creative Professionals
            </h2>
            <div className="flex justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <p className="text-body text-body-grey font-body">4.9/5 average rating from 10,000+ users</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div key={testimonial.name} className="bg-background rounded-card p-8 shadow-card">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-body text-body-grey mb-6 italic font-body">"{testimonial.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="text-3xl">{testimonial.avatar}</div>
                  <div>
                    <p className="font-body font-medium text-text-dark">{testimonial.name}</p>
                    <p className="text-label text-body-grey font-body">{testimonial.role} at {testimonial.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-h2 text-text-dark mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          
          <div className="space-y-6">
            {faqs.map((faq) => (
              <details key={faq.question} className="bg-white rounded-card p-6 shadow-card cursor-pointer group">
                <summary className="font-body font-medium text-body text-text-dark flex justify-between items-center">
                  {faq.question}
                  <span className="text-2xl text-body-grey group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-4 text-body text-body-grey font-body">{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 bg-accent-gradient text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-h2 mb-6">
            Ready to Create Amazing Mockups?
          </h2>
          <p className="text-xl mb-10 opacity-90 font-body">
            Join 10,000+ brands creating professional mockups with AI
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-brand-purple font-body font-medium text-button-lg rounded-lg hover:shadow-lg transform hover:scale-105 transition-all"
            >
              <Zap className="w-5 h-5" />
              Get Started Now
              <ArrowRight className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-4 text-white/80">
              <CheckCircle className="w-5 h-5" />
              <span className="font-body">No credit card required</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;